/**
 * Speed-to-Lead Intelligence Engine
 * 
 * Tracks response time for every lead, enforces SLA thresholds,
 * provides priority scoring, and auto-assignment logic.
 */

import type { Opportunity } from "./opportunityTypes";
import type { Conversation, Message } from "./communicationTypes";

/* ═══════ SLA THRESHOLDS ═══════ */

export type SLAStatus = "instant" | "on_time" | "at_risk" | "missed";

export interface SLAConfig {
  /** Max seconds for "instant" badge */
  instantThresholdSec: number;
  /** Max seconds before SLA warning */
  warningThresholdSec: number;
  /** Max seconds before SLA missed */
  missedThresholdSec: number;
}

export const DEFAULT_SLA_CONFIG: SLAConfig = {
  instantThresholdSec: 120,    // 2 min → instant
  warningThresholdSec: 300,    // 5 min → at risk
  missedThresholdSec: 900,     // 15 min → missed
};

export const SOURCE_SLA_OVERRIDES: Record<string, Partial<SLAConfig>> = {
  "Google Ads":   { instantThresholdSec: 60,  warningThresholdSec: 120, missedThresholdSec: 300 },
  "Facebook Ads": { instantThresholdSec: 60,  warningThresholdSec: 120, missedThresholdSec: 300 },
  "Angi":         { instantThresholdSec: 120, warningThresholdSec: 300, missedThresholdSec: 600 },
  "HomeAdvisor":  { instantThresholdSec: 120, warningThresholdSec: 300, missedThresholdSec: 600 },
};

/* ═══════ RESPONSE TIME CALCULATION ═══════ */

export interface LeadResponseMetrics {
  leadCreatedAt: string;
  firstContactAt: string | null;
  timeToFirstContactSec: number | null;
  slaStatus: SLAStatus;
  slaLabel: string;
  isResponded: boolean;
  waitingSeconds: number;
}

export function getLeadSLAConfig(leadSource: string): SLAConfig {
  const override = SOURCE_SLA_OVERRIDES[leadSource];
  return override ? { ...DEFAULT_SLA_CONFIG, ...override } : DEFAULT_SLA_CONFIG;
}

export function calculateResponseMetrics(
  lead: Opportunity,
  conversations: Conversation[],
  messages: Message[],
): LeadResponseMetrics {
  const createdAt = lead.createdAt;
  const createdDate = parseFlexibleDate(createdAt);
  const slaConfig = getLeadSLAConfig(lead.leadSource);
  
  // Find first outbound contact
  const convo = conversations.find((c) => c.linkedLeadId === lead.id || c.linkedQuoteId === lead.id);
  let firstContactAt: string | null = null;
  
  if (convo) {
    const outboundMsgs = messages
      .filter((m) => m.conversationId === convo.id && m.direction === "outbound")
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    if (outboundMsgs.length > 0) {
      firstContactAt = outboundMsgs[0].timestamp;
    }
  }

  // Also check activities for call/contact records
  if (!firstContactAt) {
    const contactActivities = lead.activities
      .filter((a) => a.type === "call" || a.description.toLowerCase().includes("contact") || a.description.toLowerCase().includes("called"))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    if (contactActivities.length > 0) {
      firstContactAt = contactActivities[0].createdAt;
    }
  }

  const now = new Date();
  let timeToFirstContactSec: number | null = null;
  let waitingSeconds = Math.floor((now.getTime() - createdDate.getTime()) / 1000);
  
  if (firstContactAt) {
    const contactDate = parseFlexibleDate(firstContactAt);
    timeToFirstContactSec = Math.max(0, Math.floor((contactDate.getTime() - createdDate.getTime()) / 1000));
    waitingSeconds = 0;
  }

  const effectiveSeconds = timeToFirstContactSec ?? waitingSeconds;
  const slaStatus = deriveSLAStatus(effectiveSeconds, slaConfig, !!firstContactAt);

  return {
    leadCreatedAt: createdAt,
    firstContactAt,
    timeToFirstContactSec,
    slaStatus,
    slaLabel: formatSLALabel(slaStatus, effectiveSeconds, !!firstContactAt),
    isResponded: !!firstContactAt,
    waitingSeconds: firstContactAt ? 0 : waitingSeconds,
  };
}

function deriveSLAStatus(seconds: number, config: SLAConfig, isResponded: boolean): SLAStatus {
  if (isResponded) {
    if (seconds <= config.instantThresholdSec) return "instant";
    if (seconds <= config.warningThresholdSec) return "on_time";
    if (seconds <= config.missedThresholdSec) return "at_risk";
    return "missed";
  }
  // Not yet responded — based on wait time
  if (seconds <= config.warningThresholdSec) return "on_time";
  if (seconds <= config.missedThresholdSec) return "at_risk";
  return "missed";
}

function formatSLALabel(status: SLAStatus, seconds: number, isResponded: boolean): string {
  const timeStr = formatDuration(seconds);
  if (isResponded) {
    if (status === "instant") return `⚡ Responded in ${timeStr}`;
    if (status === "on_time") return `Responded in ${timeStr}`;
    if (status === "at_risk") return `Delayed: ${timeStr}`;
    return `❌ Missed SLA: ${timeStr}`;
  }
  if (status === "on_time") return `Waiting: ${timeStr}`;
  if (status === "at_risk") return `⏳ At Risk: ${timeStr}`;
  return `❌ SLA Missed: ${timeStr}`;
}

function formatDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const min = Math.floor(totalSeconds / 60);
  if (min < 60) return `${min} min`;
  const hrs = Math.floor(min / 60);
  const remMin = min % 60;
  if (hrs < 24) return remMin > 0 ? `${hrs}h ${remMin}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function parseFlexibleDate(dateStr: string): Date {
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d;
  // Handle date-only strings by treating as start of day
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return new Date();
}

/* ═══════ SLA DISPLAY CONFIG ═══════ */

export const SLA_BADGE_CONFIG: Record<SLAStatus, { label: string; color: string; bg: string; icon: string }> = {
  instant:  { label: "Instant",  color: "text-success", bg: "bg-success/10 border-success/30", icon: "⚡" },
  on_time:  { label: "On Time",  color: "text-info",    bg: "bg-info/10 border-info/30",       icon: "✓" },
  at_risk:  { label: "At Risk",  color: "text-warning",   bg: "bg-warning/10 border-warning/30",     icon: "⏳" },
  missed:   { label: "Missed",   color: "text-destructive", bg: "bg-destructive/10 border-destructive/20", icon: "❌" },
};

/* ═══════ PRIORITY SCORING ═══════ */

export interface LeadPriorityScore {
  score: number;        // 0-100
  label: string;
  reasons: string[];
}

export function calculateLeadPriority(
  lead: Opportunity,
  metrics: LeadResponseMetrics,
): LeadPriorityScore {
  let score = 50;
  const reasons: string[] = [];

  // SLA urgency
  if (!metrics.isResponded) {
    if (metrics.slaStatus === "missed") { score += 30; reasons.push("SLA missed — respond immediately"); }
    else if (metrics.slaStatus === "at_risk") { score += 20; reasons.push("SLA at risk"); }
    else { score += 10; reasons.push("New lead awaiting response"); }
  }

  // Value weighting
  if (lead.totalEstimate >= 20000) { score += 15; reasons.push("High-value opportunity"); }
  else if (lead.totalEstimate >= 10000) { score += 8; reasons.push("Mid-value opportunity"); }

  // Hot sources get priority
  const hotSources = ["Google Ads", "Facebook Ads", "Angi"];
  if (hotSources.includes(lead.leadSource)) { score += 5; reasons.push(`Hot source: ${lead.leadSource}`); }

  // Recency
  const ageMs = Date.now() - parseFlexibleDate(lead.createdAt).getTime();
  const ageHours = ageMs / (1000 * 60 * 60);
  if (ageHours < 1) { score += 10; reasons.push("Created within last hour"); }
  else if (ageHours < 4) { score += 5; reasons.push("Created today"); }

  return { score: Math.min(100, score), label: score >= 80 ? "Critical" : score >= 60 ? "High" : score >= 40 ? "Medium" : "Low", reasons };
}

/* ═══════ AUTO-ASSIGNMENT ═══════ */

export interface RepPerformance {
  rep: string;
  avgResponseTimeSec: number;
  totalLeads: number;
  activeLeads: number;
  closeRate: number;
}

export function getRepPerformance(
  leads: Opportunity[],
  conversations: Conversation[],
  messages: Message[],
): RepPerformance[] {
  const repMap = new Map<string, { totalResponseSec: number; respondedCount: number; totalLeads: number; activeLeads: number; wonLeads: number; completedLeads: number }>();

  leads.forEach((lead) => {
    const rep = lead.assignedRep;
    if (!rep) return;
    if (!repMap.has(rep)) repMap.set(rep, { totalResponseSec: 0, respondedCount: 0, totalLeads: 0, activeLeads: 0, wonLeads: 0, completedLeads: 0 });
    const data = repMap.get(rep)!;
    data.totalLeads++;
    if (lead.stage !== "Closed Won" && lead.stage !== "Closed Lost") data.activeLeads++;
    if (lead.stage === "Closed Won") { data.wonLeads++; data.completedLeads++; }
    if (lead.stage === "Closed Lost") data.completedLeads++;

    const metrics = calculateResponseMetrics(lead, conversations, messages);
    if (metrics.timeToFirstContactSec !== null) {
      data.totalResponseSec += metrics.timeToFirstContactSec;
      data.respondedCount++;
    }
  });

  return Array.from(repMap.entries()).map(([rep, data]) => ({
    rep,
    avgResponseTimeSec: data.respondedCount > 0 ? Math.round(data.totalResponseSec / data.respondedCount) : Infinity,
    totalLeads: data.totalLeads,
    activeLeads: data.activeLeads,
    closeRate: data.completedLeads > 0 ? Math.round((data.wonLeads / data.completedLeads) * 100) : 0,
  }));
}

export function recommendAssignment(
  reps: RepPerformance[],
  _roundRobinIndex: number,
): { rep: string; reason: string; method: "performance" | "availability" | "round_robin" } {
  if (reps.length === 0) return { rep: "Unassigned", reason: "No reps available", method: "round_robin" };

  // 1. Try best performer with fewest active leads
  const eligible = reps.filter((r) => r.activeLeads < 15);
  if (eligible.length > 0) {
    // Score: lower active count + higher close rate + lower response time
    const scored = eligible.map((r) => ({
      ...r,
      score: (100 - r.activeLeads * 5) + r.closeRate + (r.avgResponseTimeSec < 300 ? 20 : 0),
    })).sort((a, b) => b.score - a.score);

    const best = scored[0];
    if (best.closeRate > 20) {
      return { rep: best.rep, reason: `Best performer (${best.closeRate}% close rate, ${best.activeLeads} active)`, method: "performance" };
    }
    if (best.activeLeads === Math.min(...eligible.map((r) => r.activeLeads))) {
      return { rep: best.rep, reason: `Most available (${best.activeLeads} active leads)`, method: "availability" };
    }
  }

  // 2. Round robin fallback
  const idx = _roundRobinIndex % reps.length;
  return { rep: reps[idx].rep, reason: "Round robin assignment", method: "round_robin" };
}

/* ═══════ AGGREGATE METRICS ═══════ */

export interface SpeedToLeadSummary {
  avgResponseTimeSec: number;
  medianResponseTimeSec: number;
  instantCount: number;
  onTimeCount: number;
  atRiskCount: number;
  missedCount: number;
  unrespondedCount: number;
  totalLeads: number;
  slaComplianceRate: number;
}

export function calculateSpeedToLeadSummary(
  leads: Opportunity[],
  conversations: Conversation[],
  messages: Message[],
): SpeedToLeadSummary {
  const activeLeads = leads.filter((l) => l.stage === "Lead");
  const allMetrics = activeLeads.map((l) => calculateResponseMetrics(l, conversations, messages));

  const responseTimes = allMetrics
    .filter((m) => m.timeToFirstContactSec !== null)
    .map((m) => m.timeToFirstContactSec!);

  const sorted = [...responseTimes].sort((a, b) => a - b);
  const avg = responseTimes.length > 0 ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length) : 0;
  const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0;

  return {
    avgResponseTimeSec: avg,
    medianResponseTimeSec: median,
    instantCount: allMetrics.filter((m) => m.slaStatus === "instant").length,
    onTimeCount: allMetrics.filter((m) => m.slaStatus === "on_time").length,
    atRiskCount: allMetrics.filter((m) => m.slaStatus === "at_risk").length,
    missedCount: allMetrics.filter((m) => m.slaStatus === "missed").length,
    unrespondedCount: allMetrics.filter((m) => !m.isResponded).length,
    totalLeads: activeLeads.length,
    slaComplianceRate: allMetrics.length > 0
      ? Math.round(((allMetrics.filter((m) => m.slaStatus === "instant" || m.slaStatus === "on_time").length) / allMetrics.length) * 100)
      : 100,
  };
}
